import React, { useState } from 'react';
import { matchPath, NavLink as RouterLink, useLocation } from 'react-router-dom';

import { Collapse, ListItem, makeStyles, useTheme } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import PropTypes from 'prop-types';

// Components
import { Mui } from 'components';

const useStyles = makeStyles(theme => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
  },
  button: {
    color: theme.palette.text.secondary,
    padding: '10px 0px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    borderRadius: 0,
    '&:hover': {
      backgroundColor: theme.palette.brand.grey[100],
    },
    '&:hover $favoriteStar': {
      opacity: 1,
    },
  },
  buttonLeaf: {
    color: theme.palette.text.secondary,
    padding: '10px 0px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    borderRadius: 0,
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      '& $title': {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.brand.grey[200],
    },
    '&:hover $favoriteStar': {
      opacity: 1,
    },
  },
  railButton: {
    justifyContent: 'center',
    padding: '10px 0px',
    height: '45.141px',
    minWidth: 0,
    borderRadius: 0,
    '&:hover': {
      backgroundColor: theme.palette.brand.grey[200],
    },
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  railIcon: {
    marginRight: 0,
  },
  fallbackAvatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: 'uppercase',
  },
  cornerBadge: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    height: 16,
    minWidth: 16,
    padding: 0,
    fontSize: 10,
  },
  title: {
    marginRight: 'auto',
  },
  favoriteStar: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    marginLeft: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease',
    color: theme.palette.text.disabled,
    '&:hover': {
      color: theme.palette.warning.main,
      backgroundColor: theme.palette.grey[300],
    },
  },
  favoriteStarActive: {
    opacity: 1,
    color: theme.palette.warning.main,
  },
  newBadge: {
    backgroundColor: ({ accentColor }) => accentColor || theme.palette.primary.main,
  },
}));

function FavoriteStar({ classes, favoriteable, isFavorite, onFavoriteToggle }) {
  const [hovered, setHovered] = useState(false);

  if (!favoriteable) return null;

  const handleClick = event => {
    event.preventDefault();
    event.stopPropagation();
    onFavoriteToggle?.();
  };

  const handleKeyDown = event => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    onFavoriteToggle?.();
  };

  const iconName = isFavorite === hovered ? 'star_border' : 'star';

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <span
      role="button"
      tabIndex={0}
      aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
      className={clsx(classes.favoriteStar, isFavorite && classes.favoriteStarActive)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}>
      <Mui.Icon fontSize="small">{iconName}</Mui.Icon>
    </span>
  );
}

FavoriteStar.propTypes = {
  classes: PropTypes.object.isRequired,
  favoriteable: PropTypes.bool,
  isFavorite: PropTypes.bool,
  onFavoriteToggle: PropTypes.func,
};

function RailGlyph({ icon, title, badgeContent, isNew, isFavorite, classes }) {
  const overlap = icon ? 'rectangle' : 'circle';

  let glyph;
  if (icon) glyph = icon({ className: clsx(classes.icon, classes.railIcon), size: 20 });
  else {
    const fallbackLetter = typeof title === 'string' ? title.charAt(0) : '';
    glyph = <span className={classes.fallbackAvatar}>{fallbackLetter}</span>;
  }

  const showTopDot = badgeContent == null && isNew;

  return (
    <Mui.Badge
      invisible={!isFavorite}
      badgeContent={<Mui.Icon fontSize="inherit">star</Mui.Icon>}
      overlap={overlap}
      classes={{ badge: classes.cornerBadge }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <Mui.Badge
        invisible={badgeContent == null && !showTopDot}
        badgeContent={badgeContent}
        variant={badgeContent == null ? 'dot' : 'standard'}
        classes={badgeContent == null ? { badge: classes.newBadge } : undefined}
        overlap={overlap}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}>
        {glyph}
      </Mui.Badge>
    </Mui.Badge>
  );
}

RailGlyph.propTypes = {
  icon: PropTypes.any,
  title: PropTypes.any,
  badgeContent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isNew: PropTypes.bool,
  isFavorite: PropTypes.bool,
  classes: PropTypes.object.isRequired,
};

function buildActiveStyle(isActive, style, activeColor, theme) {
  return {
    buttonStyle: isActive ? { ...style, color: activeColor } : style,
    titleStyle: isActive ? { fontWeight: theme.typography.fontWeightMedium } : undefined,
  };
}

function BranchItem({
  title,
  className,
  style,
  children,
  icon,
  menuIcon,
  favoriteStar,
  openProp,
  onToggle,
  submenuIndicator,
  onMouseEnter,
  onMouseLeave,
  isActive,
  activeColor,
  classes,
  rest,
}) {
  const theme = useTheme();
  const isFlyoutTrigger = submenuIndicator === 'flyout';
  const handleToggle = event => onToggle?.(event);

  let submenuIcon = <ExpandMoreIcon size="small" color="inherit" />;
  if (isFlyoutTrigger) submenuIcon = <ChevronRightIcon size="small" color="inherit" />;
  else if (openProp) submenuIcon = <ExpandLessIcon size="small" color="inherit" />;

  const { buttonStyle, titleStyle } = buildActiveStyle(isActive, style, activeColor, theme);

  return (
    <ListItem className={clsx(classes.item, className)} disableGutters key={title} {...rest}>
      <Mui.Button
        variant="text"
        color="default"
        className={classes.button}
        onClick={handleToggle}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        aria-haspopup={isFlyoutTrigger || undefined}
        aria-expanded={isFlyoutTrigger ? openProp : undefined}
        data-flyout-trigger={isFlyoutTrigger || undefined}
        style={buttonStyle}>
        {icon && menuIcon(isActive)}
        <span className={classes.title} style={titleStyle}>
          {title}
        </span>
        {favoriteStar}
        {submenuIcon}
      </Mui.Button>
      {!isFlyoutTrigger && <Collapse in={openProp}>{children}</Collapse>}
    </ListItem>
  );
}

BranchItem.propTypes = {
  title: PropTypes.any,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
  icon: PropTypes.any,
  menuIcon: PropTypes.func.isRequired,
  favoriteStar: PropTypes.node,
  openProp: PropTypes.bool,
  onToggle: PropTypes.func,
  submenuIndicator: PropTypes.oneOf(['collapse', 'flyout']).isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  isActive: PropTypes.bool,
  activeColor: PropTypes.string,
  classes: PropTypes.object.isRequired,
  rest: PropTypes.object,
};

function NavItem(props) {
  const {
    title,
    href,
    depth,
    children,
    isBranch,
    icon,
    className,
    open: openProp = false,
    onToggle,
    submenuIndicator,
    onMouseEnter,
    onMouseLeave,
    isActive,
    favoriteable,
    isFavorite,
    onFavoriteToggle,
    info,
    target,
    disabled,
    isNew,
    isStaff,
    accentColor,
    railMode,
    badgeContent,
    ...rest
  } = props;

  // Hooks
  const classes = useStyles({ accentColor });
  const theme = useTheme();
  const location = useLocation();

  // Constants
  let paddingLeft;
  if (depth > 0) {
    paddingLeft = 24 + 8 * depth;
  }
  const style = { paddingLeft };
  const activeColor = accentColor || theme.palette.primary.main;

  // Actions
  const menuIcon = isActiveIcon => {
    const iconStyle = isActiveIcon ? { color: activeColor } : undefined;

    return isStaff || isNew ? (
      <Mui.Badge
        color={isStaff ? 'error' : undefined}
        classes={!isStaff && isNew ? { badge: classes.newBadge } : undefined}
        variant="dot"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}>
        {icon({ className: classes.icon, size: 20, style: iconStyle })}
      </Mui.Badge>
    ) : (
      icon({ className: classes.icon, size: 20, style: iconStyle })
    );
  };

  const favoriteStar = (
    <FavoriteStar
      classes={classes}
      favoriteable={favoriteable}
      isFavorite={isFavorite}
      onFavoriteToggle={onFavoriteToggle}
    />
  );

  if (isBranch || children) {
    return (
      <BranchItem
        title={title}
        className={className}
        style={style}
        icon={icon}
        menuIcon={menuIcon}
        favoriteStar={favoriteStar}
        openProp={openProp}
        onToggle={onToggle}
        submenuIndicator={submenuIndicator}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        isActive={isActive}
        activeColor={activeColor}
        classes={classes}
        rest={rest}>
        {children}
      </BranchItem>
    );
  }

  if (!href) console.warn(`NavItem: leaf item "${title}" is missing href`);

  const isExternalLink = Boolean(href?.includes('http'));
  const isActiveLeaf =
    !isExternalLink && Boolean(href && matchPath(location.pathname, { path: href, exact: true }));

  const routeProps = {
    to: href,
    component: RouterLink,
    exact: true,
    activeClassName: '',
  };

  const linkProps = { component: 'a', href };

  const routerProps = isExternalLink ? linkProps : routeProps;

  const { buttonStyle: leafButtonStyle, titleStyle: leafTitleStyle } = buildActiveStyle(
    isActiveLeaf,
    style,
    activeColor,
    theme,
  );

  const leafButton = (
    <Mui.Button
      variant="text"
      color="default"
      className={clsx(classes.buttonLeaf, `depth-${depth}`, railMode && classes.railButton)}
      disabled={disabled}
      style={leafButtonStyle}
      target={target}
      {...routerProps}>
      {railMode ? (
        <RailGlyph
          icon={icon}
          title={title}
          badgeContent={badgeContent}
          isNew={isNew}
          isFavorite={isFavorite}
          classes={classes}
        />
      ) : (
        <>
          {icon && menuIcon(isActiveLeaf)}
          <span className={classes.title} style={leafTitleStyle}>
            {!icon && (isStaff || isNew) ? (
              <Mui.Badge
                color={isStaff ? 'error' : undefined}
                classes={!isStaff && isNew ? { badge: classes.newBadge } : undefined}
                variant="dot"
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}>
                {title}
              </Mui.Badge>
            ) : (
              title
            )}
          </span>
          {info && info({})}
          {favoriteStar}
        </>
      )}
    </Mui.Button>
  );

  return (
    <ListItem className={clsx(classes.itemLeaf, className)} disableGutters key={title} {...rest}>
      {railMode ? <Mui.Tooltip title={title}>{leafButton}</Mui.Tooltip> : leafButton}
    </ListItem>
  );
}

NavItem.propTypes = {
  children: PropTypes.node,
  isBranch: PropTypes.bool,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  target: PropTypes.string,
  icon: PropTypes.any,
  info: PropTypes.any,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
  submenuIndicator: PropTypes.oneOf(['collapse', 'flyout']),
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  isActive: PropTypes.bool,
  favoriteable: PropTypes.bool,
  isFavorite: PropTypes.bool,
  onFavoriteToggle: PropTypes.func,
  disabled: PropTypes.bool,
  title: PropTypes.any.isRequired,
  isNew: PropTypes.bool,
  isStaff: PropTypes.bool,
  accentColor: PropTypes.string,
  railMode: PropTypes.bool,
  badgeContent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

NavItem.defaultProps = {
  open: false,
  isBranch: false,
  submenuIndicator: 'collapse',
  isActive: false,
  isNew: false,
  isStaff: false,
  favoriteable: false,
  isFavorite: false,
  railMode: false,
};

export default NavItem;
